/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * The ⓘ beside a fitness profile: how the score is calculated. Shown only once
 * scoring reads tested results (movementStateRead), because before that the
 * explanation would not be true.
 */

import React, { useId, useState } from "react";
import {
  Button,
  InfoIcon,
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from "@lemonadetraining/web-ui";
import { isMovementStateReadLive } from "@lemonadetraining/contracts";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { RADAR_EXPLAINER_COPY, type RadarExplainerSubject } from "./radarExplainerCopy";

interface Props {
  subject: RadarExplainerSubject;
}

const RadarExplainerPopover: React.FC<Props> = ({ subject }) => {
  const { flags, isLoading } = useFeatureFlags(true);
  const [open, setOpen] = useState(false);
  const titleID = useId();

  if (isLoading || !isMovementStateReadLive(flags)) return null;

  const copy = RADAR_EXPLAINER_COPY[subject];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          className="size-11 text-muted-foreground hover:text-foreground md:size-6"
          aria-label={copy.title}
          data-testid="radar-explainer-trigger"
        >
          <InfoIcon className="size-4" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={16}
        aria-labelledby={titleID}
        className="w-80 max-w-[calc(100vw-2rem)]"
        data-testid="radar-explainer"
        // Inside a Sheet or Dialog, Radix can briefly lose track of which layer
        // is on top and drop the Escape; this closes the popover regardless.
        onKeyDown={(event) => {
          if (event.key !== "Escape" || event.defaultPrevented) return;
          event.preventDefault();
          setOpen(false);
        }}
      >
        <PopoverHeader>
          <PopoverTitle id={titleID} className="font-semibold text-foreground">
            {copy.title}
          </PopoverTitle>
        </PopoverHeader>
        <p className="text-muted-foreground">{copy.intro}</p>
        <ul className="list-disc space-y-2 pl-5 text-muted-foreground">
          {copy.bullets.map((bullet) => (
            <li key={bullet}>{bullet}</li>
          ))}
        </ul>
        <p className="text-foreground">{copy.closing}</p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="min-h-11 self-end"
          onClick={() => setOpen(false)}
        >
          Got it
        </Button>
      </PopoverContent>
    </Popover>
  );
};

export default RadarExplainerPopover;
